import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Tag, ChevronRight, Copy, Check } from "lucide-react";

export default function Coupons() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/coupons");
        if (!cancelled) setCoupons(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCoupons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user) load();
    else setLoading(false);
    return () => { cancelled = true; };
  }, [user]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(lang === "ar" ? "تم نسخ الكود" : "Code copied");
      setTimeout(() => setCopied(null), 1800);
    } catch {
      toast.error(lang === "ar" ? "تعذّر النسخ" : "Could not copy");
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 pt-6 pb-28 bg-[#FAFAFA] min-h-screen" data-testid="coupons-page">
      <div className="flex items-center gap-2 mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-[#EEE] flex items-center justify-center"
          data-testid="coupons-back"
        >
          <ChevronRight className={`w-5 h-5 text-[#1A1A1A] ${lang === "ar" ? "" : "rotate-180"}`} />
        </button>
        <h1 className="font-display text-2xl font-black text-[#1A1A1A]">
          {lang === "ar" ? "قسائمي" : "My Coupons"}
        </h1>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="coupons-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-4 border border-[#EAEAEA] animate-pulse h-[104px]" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-16" data-testid="coupons-empty">
          <div className="inline-flex w-24 h-24 rounded-full bg-[#FEF0EE] items-center justify-center mb-5">
            <Tag className="w-11 h-11 text-[#E05D3A]" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-xl font-black text-[#1A1A1A] mb-2">
            {lang === "ar" ? "لا توجد قسائم حالياً" : "No coupons available"}
          </h2>
          <p className="text-[#6B6B6B] text-sm max-w-xs mx-auto">
            {lang === "ar" ? "تابعنا للحصول على عروض وخصومات حصرية" : "Stay tuned for exclusive deals"}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="coupons-list">
          {coupons.map((c) => (
            <div
              key={c.code}
              className="relative bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden flex"
              data-testid={`coupon-${c.code}`}
            >
              {/* Left value badge */}
              <div className="w-20 flex-shrink-0 bg-[#FEF0EE] flex flex-col items-center justify-center text-center px-2">
                <Tag className="w-5 h-5 text-[#E05D3A] mb-1" strokeWidth={2.2} />
                <span className="text-[15px] font-black text-[#E05D3A] leading-none">
                  {c.discount_type === "percent" ? `${c.value}%` : `${c.value}`}
                </span>
                {c.discount_type !== "percent" && (
                  <span className="text-[10px] font-bold text-[#E05D3A]">{lang === "ar" ? "ر.س" : "SAR"}</span>
                )}
              </div>
              {/* Body */}
              <div className="flex-1 min-w-0 p-3.5">
                <h3 className="font-bold text-[14px] text-[#1A1A1A] truncate">
                  {lang === "ar" ? c.title_ar : c.title_en}
                </h3>
                <p className="text-xs text-[#6B6B6B] mt-1 line-clamp-2">
                  {lang === "ar" ? c.desc_ar : c.desc_en}
                </p>
                <button
                  type="button"
                  onClick={() => copyCode(c.code)}
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#E05D3A] text-[#E05D3A] px-3 py-1 text-[12px] font-bold active:bg-[#FEF0EE]"
                  data-testid={`coupon-copy-${c.code}`}
                >
                  {copied === c.code ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {c.code}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
